import {
  Box,
  Stepper as MuiStepper,
  Step,
  StepConnector,
  StepIconProps,
  StepLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import * as Icons from '../icons';

interface StepperProps {
  activeStep: number;
  steps: string[];
}

const CustomStepIcon = (props: StepIconProps) => {
  const { active, completed, icon } = props;

  if (completed) {
    return (
      <Box
        sx={{
          width: '32px',
          height: '32px',
          borderRadius: '100px',
          backgroundColor: 'brand.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icons.Check sx={{ width: '20px', height: '20px', color: 'common.white' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '32px',
        height: '32px',
        borderRadius: '100px',
        backgroundColor: active ? 'brand.main' : 'grey.500',
        color: 'common.white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& .MuiStepIcon-text': {
          fontFamily: '"Source Sans Pro"',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          fill: 'currentColor',
        },
      }}
    >
      <span
        style={{
          fontFamily: '"Source Sans Pro"',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
        }}
      >
        {icon}
      </span>
    </Box>
  );
};

export const Stepper = ({ activeStep, steps }: StepperProps) => {
  const theme = useTheme();
  const isVeryNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  return (
    <Box sx={{ width: '100%' }}>
      <MuiStepper
        data-testid="stepper"
        activeStep={activeStep}
        orientation={isVeryNarrow ? 'vertical' : 'horizontal'}
        sx={{
          width: '100%',
          '& .MuiStep-root': {
            padding: 0,
            margin: 0,
            ...(isNarrow && {
              '&:not(:last-child)': {
                marginRight: '12px',
              },
            }),
            ...(isVeryNarrow && {
              '&:not(:last-child)': {
                marginBottom: '12px',
              },
            }),
          },
          '& .MuiSvgIcon-root.MuiStepIcon-completedIcon': {
            width: '20px !important',
            height: '20px !important',
          },
          '& .MuiStepLabel-root': {
            padding: 0,
            alignItems: 'center',
            '& .MuiStepLabel-iconContainer': {
              marginRight: 0,
              '& .MuiStepIcon-root': {
                width: '32px',
                height: '32px',
                color: 'grey.500',
                '&.Mui-active, &.Mui-completed': {
                  color: 'brand.main',
                },
                '& text': {
                  fill: 'common.white',
                  fontFamily: '"Source Sans Pro"',
                  fontSize: '14px',
                  fontWeight: 600,
                  alignmentBaseline: 'middle',
                  textAnchor: 'middle',
                },
                '& .MuiStepIcon-completedIcon': {
                  fontSize: '20px !important',
                  width: '20px !important',
                  height: '20px !important',
                },
                '& .MuiSvgIcon-root': {
                  width: '20px !important',
                  height: '20px !important',
                },
              },
            },
            '& .MuiStepLabel-label': {
              color: 'grey.800',
              fontFamily: '"Source Sans Pro"',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: 'normal',
              '&.Mui-active': {
                color: 'grey.1000',
                fontWeight: 600,
              },
              '&.Mui-completed': {
                color: 'grey.800',
                fontWeight: 400,
              },
            },
          },
        }}
        connector={
          <StepConnector
            sx={{
              display: isNarrow ? 'none' : 'block',
              '& .MuiStepConnector-line': {
                background: '#BDBDBD',
                height: '1px',
                flex: '1 0 0',
                border: 'none',
                margin: '0 12px',
              },
            }}
          />
        }
      >
        {steps.map((label, index) => (
          <Step key={label} completed={index < activeStep} data-testid="step">
            <StepLabel StepIconComponent={CustomStepIcon}>{label}</StepLabel>
          </Step>
        ))}
      </MuiStepper>
    </Box>
  );
};
